import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { CameraService } from '../camera/camera.service';

interface ActiveAudioCall {
  pc: RTCPeerConnection;
  audioCtx: AudioContext;
  source: AudioBufferSourceNode | null;
  sourceStarted: boolean;
  cancelled: boolean;
  controller: AbortController;
  subscriber: Subscriber<void>;
}

@Injectable({ providedIn: 'root' })
export class WebRtcAudioService {
  private cameraService = inject(CameraService);
  private activeCalls = new Map<string, ActiveAudioCall>();

  sendAudio(cameraId: string, audioUrl: string): Observable<void> {
    return new Observable<void>(subscriber => {
      this.stopAudioForCamera(cameraId);

      const controller = new AbortController();
      const pc = new RTCPeerConnection();
      const audioCtx = new AudioContext();

      const call: ActiveAudioCall = {
        pc,
        audioCtx,
        source: null,
        sourceStarted: false,
        cancelled: false,
        controller,
        subscriber,
      };
      this.activeCalls.set(cameraId, call);

      fetch(audioUrl, { signal: controller.signal })
        .then(res => {
          this.checkNotCancelled(call);
          return res.arrayBuffer();
        })
        .then(buf => {
          this.checkNotCancelled(call);
          return audioCtx.decodeAudioData(buf);
        })
        .then(audioBuffer => {
          this.checkNotCancelled(call);

          const dest = audioCtx.createMediaStreamDestination();
          const source = audioCtx.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(dest);
          source.onended = () => {
            if (!call.cancelled && !subscriber.closed) {
              subscriber.next();
              subscriber.complete();
            }
          };
          call.source = source;

          dest.stream.getAudioTracks().forEach(track => {
            pc.addTrack(track, dest.stream);
          });

          return pc.createOffer();
        })
        .then(offer => pc.setLocalDescription(offer))
        .then(() => this.waitForIce(call))
        .then(() => this.lastValueWithAbort(this.cameraService.sendAudio(cameraId, pc.localDescription!.sdp), controller.signal))
        .then(sdpAnswer => {
          this.checkNotCancelled(call);
          const answer = JSON.parse(sdpAnswer);
          return pc.setRemoteDescription(answer);
        })
        .then(() => this.waitForConnection(call))
        .then(() => {
          if (!call.cancelled && !subscriber.closed && call.source) {
            call.source.start();
            call.sourceStarted = true;
          }
        })
        .catch(err => {
          if (!call.cancelled && !subscriber.closed) {
            subscriber.error(err);
          }
        });

      return () => {
        if (this.activeCalls.get(cameraId) === call) {
          this.activeCalls.delete(cameraId);
        }
        this.closeCall(call);
      };
    });
  }

  stopAudio() {
    this.activeCalls.forEach(call => {
      this.closeCall(call);
    });
    this.activeCalls.clear();
  }

  private stopAudioForCamera(cameraId: string) {
    const existing = this.activeCalls.get(cameraId);
    if (!existing) {
      return;
    }
    this.closeCall(existing);
    this.activeCalls.delete(cameraId);
  }

  private closeCall(call: ActiveAudioCall) {
    call.cancelled = true;
    call.controller.abort();

    if (call.sourceStarted) {
      try {
        call.source?.stop();
      } catch {
        // Already stopped or finished; ignore.
      }
    }

    call.pc.close();
    call.audioCtx.close().catch(() => {
      // AudioContext may already be closed; ignore.
    });

    if (!call.subscriber.closed) {
      call.subscriber.complete();
    }
  }

  private checkNotCancelled(call: ActiveAudioCall) {
    if (call.cancelled) {
      throw new Error('cancelled');
    }
  }

  private waitForConnection(call: ActiveAudioCall): Promise<void> {
    return new Promise((resolve, reject) => {
      const pc = call.pc;

      if (pc.connectionState === 'connected') {
        resolve();
        return;
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        reject(new Error(`WebRTC connection ${pc.connectionState}`));
        return;
      }
      if (call.cancelled) {
        reject(new Error('cancelled'));
        return;
      }

      const handler = () => {
        if (call.cancelled) {
          pc.removeEventListener('connectionstatechange', handler);
          reject(new Error('cancelled'));
          return;
        }
        if (pc.connectionState === 'connected') {
          pc.removeEventListener('connectionstatechange', handler);
          resolve();
          return;
        }
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          pc.removeEventListener('connectionstatechange', handler);
          reject(new Error(`WebRTC connection ${pc.connectionState}`));
          return;
        }
      };

      pc.addEventListener('connectionstatechange', handler);
    });
  }

  private waitForIce(call: ActiveAudioCall): Promise<void> {
    return new Promise((resolve, reject) => {
      const pc = call.pc;

      if (pc.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      if (pc.connectionState === 'closed' || call.cancelled) {
        reject(new Error('cancelled'));
        return;
      }

      const iceHandler = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', iceHandler);
          pc.removeEventListener('connectionstatechange', connHandler);
          resolve();
        }
      };

      const connHandler = () => {
        if (pc.connectionState === 'closed' || call.cancelled) {
          pc.removeEventListener('connectionstatechange', connHandler);
          pc.removeEventListener('icegatheringstatechange', iceHandler);
          reject(new Error('cancelled'));
        }
      };

      pc.addEventListener('icegatheringstatechange', iceHandler);
      pc.addEventListener('connectionstatechange', connHandler);
    });
  }

  private lastValueWithAbort<T>(observable: Observable<T>, signal: AbortSignal): Promise<T> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const observer = {
        next: (value: T) => {
          if (!settled) {
            settled = true;
            resolve(value);
          }
        },
        error: (err: unknown) => {
          if (!settled) {
            settled = true;
            reject(err);
          }
        },
        complete: () => {
          if (!settled) {
            settled = true;
            reject(new Error('Observable completed without value'));
          }
        },
      };

      const subscription = observable.subscribe(observer);

      const onAbort = () => {
        if (!settled) {
          settled = true;
          subscription.unsubscribe();
          reject(new Error('cancelled'));
        }
      };

      if (signal.aborted) {
        onAbort();
      } else {
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }
}
